import {
  FeatureFlag,
  createManager,
} from '@gbm/snacks-feature-management-core';
import { environment } from 'src/environments/environment';

const DEFAULT_VALUES = {
  maintenanceModeHB: false,
  accountManagerHB: false,
  isAccountManagerEnableToLegacy: false,
  loadingDocumentsHB: false,
  newUrlTechRules: false,
  tradingTransfer: false,
  smartLookHB: false,
  positionPollTimer: 30,
  enableGeolocationOperations: false,
  priceAlerts: false,
  tmpExternalFundsOperable: false,
  tmpPermanenceRule: false,
  tradingUsaOperation: true,
  tmpShowUsaCapitalMovements: false,
  ujetChatHB: true,
  helpChat: true,
  tmpShowHistoricalYieldTusa: true,
  news: false,
  noFeesTrading: false,
  infoBannerContent: '',
  deleteBankAccounts: false,
  bankAccountsLimit: 1,
  authMfaCheck: false,
  tmpEnableNewWL: false,
  realTimeMinAmount: 100,
};

export type FeatureFlags = typeof DEFAULT_VALUES;

FeatureFlag.defaultProject('homebroker');
FeatureFlag.defaultWorkspace('general');

export const featureFlags = {
  maintenanceModeHB: new FeatureFlag('maintenanceModeHB').enabled(
    DEFAULT_VALUES.maintenanceModeHB,
  ),
  accountManagerHB: new FeatureFlag('accountManagerHB').enabled(
    DEFAULT_VALUES.accountManagerHB,
  ),
  loadingDocumentsHB: new FeatureFlag('loadingDocumentsHB').enabled(
    DEFAULT_VALUES.loadingDocumentsHB,
  ),
  newUrlTechRules: new FeatureFlag('newUrlTechRules').enabled(
    DEFAULT_VALUES.newUrlTechRules,
  ),
  tradingTransfer: new FeatureFlag('tradingTransfer').enabled(
    DEFAULT_VALUES.tradingTransfer,
  ),
  smartLookHB: new FeatureFlag('smartLookHB').enabled(
    DEFAULT_VALUES.smartLookHB,
  ),
  positionPollTimer: new FeatureFlag('positionPollTimer').value(
    DEFAULT_VALUES.positionPollTimer,
  ),
  priceAlerts: new FeatureFlag('priceAlerts').enabled(
    DEFAULT_VALUES.priceAlerts,
  ),
  tmpExternalFundsOperable: new FeatureFlag('tmpExternalFundsOperable').enabled(
    DEFAULT_VALUES.tmpExternalFundsOperable,
  ),
  tmpPermanenceRule: new FeatureFlag('tmpPermanenceRule').enabled(
    DEFAULT_VALUES.tmpPermanenceRule,
  ),
  tradingUsaOperation: new FeatureFlag('tradingUsaOperation').enabled(
    DEFAULT_VALUES.tradingUsaOperation,
  ),
  noFeesTrading: new FeatureFlag('noFeesTrading').enabled(
    DEFAULT_VALUES.noFeesTrading,
  ),
  deleteBankAccounts: new FeatureFlag('deleteBankAccounts').enabled(
    DEFAULT_VALUES.deleteBankAccounts,
  ),
  bankAccountsLimit: new FeatureFlag('bankAccountsLimit').value(
    DEFAULT_VALUES.bankAccountsLimit,
  ),
  authMfaCheck: new FeatureFlag('authMfaCheck').enabled(
    DEFAULT_VALUES.authMfaCheck,
  ),
  tmpEnableNewWL: new FeatureFlag('tmpEnableNewWL').enabled(
    DEFAULT_VALUES.tmpEnableNewWL,
  ),
  realTimeMinAmount: new FeatureFlag('realTimeMinAmount').value(
    DEFAULT_VALUES.realTimeMinAmount,
  ),
  ujetChatHB: new FeatureFlag('ujetChatHB').enabled(DEFAULT_VALUES.ujetChatHB),
  helpChat: new FeatureFlag('helpChat').enabled(DEFAULT_VALUES.helpChat),
  news: new FeatureFlag('news').enabled(DEFAULT_VALUES.news),
  infoBannerContent: new FeatureFlag('infoBannerContent').value(
    DEFAULT_VALUES.infoBannerContent,
  ),
  tmpShowHistoricalYieldTusa: new FeatureFlag(
    'tmpShowHistoricalYieldTusa',
  ).enabled(DEFAULT_VALUES.tmpShowHistoricalYieldTusa),
  tmpShowUsaCapitalMovements: new FeatureFlag(
    'tmpShowUsaCapitalMovements',
  ).enabled(DEFAULT_VALUES.tmpShowUsaCapitalMovements),
  enableGeolocationOperations: new FeatureFlag(
    'enableGeolocationOperations',
  ).enabled(DEFAULT_VALUES.enableGeolocationOperations),
  isAccountManagerEnableToLegacy: new FeatureFlag(
    'isAccountManagerEnableToLegacy',
  ).enabled(DEFAULT_VALUES.isAccountManagerEnableToLegacy),
};

export const initFlags = async (
  {
    apiKey,
    userEmail,
    userId,
  }: { apiKey: string; userEmail: string; userId: string },
  callback: (flags: FeatureFlags) => void,
) => {
  try {
    const envMap: Record<string, string> = {
      development: 'dev',
      stage: 'stg',
      production: 'prd',
    };

    const env =
      envMap[environment.appConfigurationEnvironment || 'development'];

    createManager({
      apiKey,
      environment: env as any,
    }).resolve(featureFlags, {
      unfreeze: 'untilOnReload',
      user: {
        email: userEmail,
        id: userId,
      },
      callback: () => callback(getFeatureFlagsSnapshot()),
    });
  } catch (err) {
    console.warn(err);
  }
};

export function getFeatureFlagsSnapshot() {
  return Object.keys(featureFlags).reduce((snapshot, key) => {
    const flagName = key as keyof FeatureFlags;
    const flag = featureFlags[flagName];

    if (typeof flag.getValue() !== 'undefined') {
      (snapshot as Record<string, any>)[flagName] = flag.properties.value;
    } else {
      (snapshot as Record<string, any>)[flagName] = flag.properties
        .enabled as boolean;
    }

    return snapshot;
  }, {} as Partial<FeatureFlags>) as FeatureFlags;
}
